import classNames from 'classnames';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';

// Title / description come from i18n (keys `<key>Title`, `<key>Description`
// under pages.home.section15).
interface Benefit {
  iconBg: string;
  iconColor: string;
  icon: ReactNode;
  key: 'b1' | 'b2' | 'b3';
}

const benefits: Benefit[] = [
  {
    iconBg: 'rgba(253,246,187,0.14)',
    iconColor: '#FDE47A',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V18h6v-1.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2zM9 22h6" />
      </svg>
    ),
    key: 'b1',
  },
  {
    iconBg: 'rgba(189,222,255,0.14)',
    iconColor: '#9cc0ff',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
    key: 'b2',
  },
  {
    iconBg: 'rgba(255,233,240,0.14)',
    iconColor: '#f5a3c2',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
      </svg>
    ),
    key: 'b3',
  },
];

export const Section15 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <div className={styles.eyebrow}>
            {t('pages.home.section15.eyebrow')}
          </div>
          <h2 className={styles.title}>{t('pages.home.section15.title')}</h2>
        </div>
        <div className={styles.grid}>
          {benefits.map((benefit) => (
            <div key={benefit.key} className={styles.benefit.container}>
              <div
                className={styles.benefit.icon}
                style={{ background: benefit.iconBg, color: benefit.iconColor }}
              >
                {benefit.icon}
              </div>
              <h3 className={styles.benefit.title}>
                {t(`pages.home.section15.${benefit.key}Title`)}
              </h3>
              <p className={styles.benefit.description}>
                {t(`pages.home.section15.${benefit.key}Description`)}
              </p>
              {/* TODO(SP-669) https://mindfeed.atlassian.net/browse/SP-669 — benefit links
            temporarily hidden for launch. Uncomment to re-enable.
            <a href={benefit.href} className={styles.benefit.link}>
              <span className={styles.benefit.linkText}>
                {benefit.linkLabel}
              </span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>
            */}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-20 pb-[30px] text-center',
  wrap: 'max-w-[1200px] mx-auto px-6 lg:px-10',
  head: 'mx-auto mb-14 max-w-[620px]',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'text-[32px] font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white lg:text-[40px]',
  grid: 'grid grid-cols-1 gap-[30px] text-left lg:grid-cols-3 lg:gap-[34px]',
  benefit: {
    container: 'flex flex-col',
    icon: 'mb-5 flex h-[52px] w-[52px] items-center justify-center rounded-[15px] [&>svg]:h-[25px] [&>svg]:w-[25px] [&>svg]:stroke-[1.7]',
    title:
      'mb-[9px] text-xl font-bold leading-[1.3] text-mf-off-black-600 marketing-dark:text-white',
    description:
      'mb-4 text-[15px] leading-[1.65] text-mf-gray-600 marketing-dark:text-white/62',
    link: classNames(
      'group mt-auto inline-flex w-fit items-center gap-2 text-sm font-semibold',
      'text-mf-blue marketing-dark:text-[#9cc0ff]',
      '[&>svg]:h-[13px] [&>svg]:w-[13px] [&>svg]:transition-transform group-hover:[&>svg]:translate-x-[3px]',
    ),
    linkText: classNames(
      'border-b-[1.5px] pb-0.5',
      'border-[#819EC6] marketing-dark:border-[#9cc0ff]/40',
    ),
  },
};
